import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ExerciseCard from '../Workspace/ExerciseCard';

export type LessonViewProps = {
  planTitle: string;
  planId: string;
  lessonIndex: number;
  lessonInstance: any;
  completionStatus?: 'not_started' | 'in_progress' | 'completed' | string;

  // Optional footer actions (used in lesson page, not workspace)
  showStickyFooter?: boolean;
  generatingExercises?: boolean;
  exercisesJobLine?: string | null;
  onGenerateExercises?: () => void;
  onDiscussWithTally?: () => void;

  // Optional hook to notify Tally when an answer is selected (workspace mode)
  onExerciseAnswered?: (payload: { lessonIndex: number; sectionIndex: number; exerciseIndex: number; answer: string; fullExercise: any }) => void;
};

const LessonView: React.FC<LessonViewProps> = ({
  planTitle,
  planId,
  lessonIndex,
  lessonInstance,
  completionStatus,
  showStickyFooter,
  generatingExercises,
  exercisesJobLine,
  onGenerateExercises,
  onDiscussWithTally,
  onExerciseAnswered,
}) => {
  const lesson = lessonInstance?.lesson || {};
  const sections: any[] = Array.isArray(lesson?.sections) ? lesson.sections : [];
  const exSections: any[] = Array.isArray(lessonInstance?.exercises?.sections) ? lessonInstance.exercises.sections : [];

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'white',
    borderRadius: '16px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.10), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    padding: '24px',
  };

  const pillButtonStyle: React.CSSProperties = {
    paddingLeft: '24px',
    paddingRight: '24px',
    paddingTop: '13px',
    paddingBottom: '13px',
    borderRadius: '100px',
    fontSize: '16px',
    fontFamily: 'Lato',
    fontWeight: 700,
    wordWrap: 'break-word',
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '6px',
  };

  const LessonExerciseCard: React.FC<{ fullExercise: any; exerciseIndex: number; sectionIndex: number }> = ({ fullExercise, exerciseIndex, sectionIndex }) => {
    const [showResult, setShowResult] = React.useState(false);
    const [userAnswer, setUserAnswer] = React.useState<string | undefined>(undefined);

    const question = fullExercise?.content?.question_text || '';
    const options = Array.isArray(fullExercise?.content?.options)
      ? fullExercise.content.options.map((o: any) => o?.answer_text).filter((x: any) => typeof x === 'string')
      : [];
    const correctAnswer = Array.isArray(fullExercise?.content?.options)
      ? (fullExercise.content.options.find((o: any) => o?.is_correct)?.answer_text as string | undefined)
      : undefined;

    return (
      <ExerciseCard
        question={question}
        options={options}
        exerciseIndex={exerciseIndex}
        onSubmit={(answer) => {
          setUserAnswer(answer);
          setShowResult(true);
          try {
            onExerciseAnswered?.({ lessonIndex, sectionIndex, exerciseIndex, answer, fullExercise });
          } catch {}
        }}
        showResult={showResult}
        correctAnswer={correctAnswer}
        userAnswer={userAnswer}
        fullExercise={fullExercise}
      />
    );
  };

  const statusPill = (() => {
    const st = completionStatus || 'not_started';
    return (
      <div
        className="text-[11px] px-2 py-0.5 rounded-full border"
        style={{
          borderColor: st === 'completed' ? '#16a34a' : st === 'in_progress' ? '#f59e0b' : '#e5e7eb',
          color: st === 'completed' ? '#166534' : st === 'in_progress' ? '#92400e' : '#6b7280',
          background: st === 'completed' ? '#dcfce7' : st === 'in_progress' ? '#fffbeb' : '#f9fafb',
          whiteSpace: 'nowrap',
        }}
      >
        {st === 'completed' ? 'Completed' : st === 'in_progress' ? 'In Progress' : 'Not Started'}
      </div>
    );
  })();

  return (
    <div className="relative">
      {onGenerateExercises && exercisesJobLine ? (
        <div className="text-xs text-gray-500 mb-3">Exercises job: {exercisesJobLine}</div>
      ) : null}

      <div className="space-y-6" style={{ paddingBottom: showStickyFooter ? 140 : 0 }}>
        {/* Overview hero */}
        <div style={{ ...cardStyle, borderLeft: '6px solid #042434' }}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Learning Plan</div>
              <div className="text-base font-bold text-gray-900">{planTitle}</div>
              <div className="text-xs text-gray-500">plan_id: {planId}</div>
            </div>
            {statusPill}
          </div>

          <div className="mt-4">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Lesson {lessonIndex + 1}
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">{String(lesson?.title || `Lesson ${lessonIndex + 1}`)}</div>
            <div className="text-sm text-gray-600">
              {typeof lesson?.estimated_minutes === 'number' ? `${lesson.estimated_minutes} minutes` : '15–20 minutes'}
            </div>
          </div>

          <div className="mt-4">
            <div className="text-sm font-semibold text-gray-900 mb-2">What you’ll learn in this lesson</div>
            {lesson?.overview ? (
              <div>
                <div className="text-sm text-gray-700 mb-3">{String(lesson?.overview?.what_you_will_learn || '')}</div>
                {Array.isArray(lesson?.overview?.outline) && lesson.overview.outline.length > 0 ? (
                  <ol className="list-decimal pl-5 text-sm text-gray-700 space-y-1">
                    {lesson.overview.outline.map((x: any, i: number) => (
                      <li key={i}>{String(x)}</li>
                    ))}
                  </ol>
                ) : (
                  <div className="text-sm text-red-700">Missing overview.outline (regenerate lesson)</div>
                )}
              </div>
            ) : (
              <div className="text-sm text-red-700">Missing overview (regenerate lesson)</div>
            )}
          </div>
        </div>

        {/* Sections + exercises */}
        {sections.map((s, i) => {
          const block = exSections.find((x: any) => x && typeof x === 'object' && x.section_index === i);
          const questions = Array.isArray(block?.questions) ? block.questions : [];

          return (
            <div key={i} className="space-y-3">
              <div style={cardStyle}>
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2">
                    <div className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: 'rgba(4,36,52,0.08)', color: '#042434' }}>
                      Section {i + 1}
                    </div>
                    <div className="text-base font-semibold text-gray-900">{String(s?.title || `Section ${i + 1}`)}</div>
                  </div>
                  {questions.length > 0 ? (
                    <div className="text-xs text-gray-500">{questions.length} exercises</div>
                  ) : (
                    <div className="text-xs text-gray-400">No exercises yet</div>
                  )}
                </div>

                <div className="prose prose-sm max-w-2xl">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      p: ({ ...props }) => <p className="mb-3" {...props} />,
                      ul: ({ ...props }) => <ul className="mb-4 list-disc pl-6" {...props} />,
                      ol: ({ ...props }) => <ol className="mb-4 list-decimal pl-6" {...props} />,
                      li: ({ ...props }) => <li className="mb-1" {...props} />,
                      table: ({ ...props }) => <table className="my-4" {...props} />,
                    }}
                  >
                    {typeof s?.content_markdown === 'string' ? s.content_markdown : ''}
                  </ReactMarkdown>
                </div>
              </div>

              <div style={{ ...cardStyle, background: 'linear-gradient(135deg, rgba(1,188,212,0.10) 0%, rgba(132,51,255,0.08) 60%, rgba(4,36,52,0.06) 100%)' }}>
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="text-sm font-semibold text-gray-900">Exercises</div>
                  {questions.length > 0 ? <div className="text-xs text-gray-600">Try them now</div> : null}
                </div>

                {questions.length > 0 ? (
                  <div className="space-y-3">
                    {questions.map((q: any, idx: number) => (
                      <div key={idx} style={{ backgroundColor: 'white', borderRadius: 12, padding: 16, border: '1px solid rgba(0,0,0,0.06)' }}>
                        <LessonExerciseCard fullExercise={q} exerciseIndex={idx} sectionIndex={i} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-700">No exercises for this section yet.</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Optional sticky footer */}
      {showStickyFooter ? (
        <div
          className="sticky bottom-0 border-t"
          style={{
            background: 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(8px)',
            borderColor: 'rgba(0,0,0,0.08)',
          }}
        >
          <div className="px-6 py-4 flex items-center justify-between gap-4">
            <div className="text-xs text-gray-500">{onDiscussWithTally ? 'Tip: “Discuss with Tally” marks this lesson completed.' : null}</div>
            <div className="flex items-center gap-3">
              {onGenerateExercises ? (
                <button
                  onClick={onGenerateExercises}
                  disabled={generatingExercises}
                  style={{
                    ...pillButtonStyle,
                    background: 'white',
                    border: '1px solid #25394D',
                    color: '#25394D',
                    opacity: generatingExercises ? 0.6 : 1,
                    cursor: generatingExercises ? 'not-allowed' : 'pointer',
                  }}
                  onMouseEnter={(e) => { if (!e.currentTarget.disabled) (e.currentTarget.style.background = '#f3f4f6'); }}
                  onMouseLeave={(e) => { if (!e.currentTarget.disabled) (e.currentTarget.style.background = 'white'); }}
                  title="Generate MCQs for this lesson"
                >
                  {generatingExercises ? 'Generating…' : 'Generate exercises'}
                </button>
              ) : null}

              <button
                onClick={onDiscussWithTally}
                disabled={!onDiscussWithTally}
                style={{
                  ...pillButtonStyle,
                  background: '#042434',
                  color: 'white',
                  opacity: (!onDiscussWithTally) ? 0.6 : 1,
                  cursor: (!onDiscussWithTally) ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={(e) => { if (!e.currentTarget.disabled) (e.currentTarget.style.background = '#063247'); }}
                onMouseLeave={(e) => { if (!e.currentTarget.disabled) (e.currentTarget.style.background = '#042434'); }}
                title="Launch Tally for this lesson"
              >
                Discuss with Tally
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default LessonView;


